import { Button, Group, type MantineColor } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipButton } from '@/components/ChipButton';

interface ConfirmActionButtonProps {
  /** Label shown before the user has tapped once, e.g. "DISABLE ACCOUNT". */
  label: string;
  /** Label shown after the first tap, prompting a second tap to proceed. */
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  color?: MantineColor;
  size?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Drops the shared elevation shadow applied to all `Button`s — used
   * inside modals, which already have their own elevation from the dialog
   * surface, so a second glowing shadow around every button reads as
   * visual noise. Page-level usages (e.g. `AdminUserDetailPage`) keep the
   * shadow for consistency with other primary buttons on that page. */
  noShadow?: boolean;
}

/**
 * Two-step confirm-before-proceeding button for destructive admin actions
 * (#2): the first tap arms it (label swaps to `confirmLabel`, style swaps
 * from outline to filled, and a CANCEL button appears alongside it); a
 * second tap actually runs `onConfirm`. Resets back to the unarmed state on
 * cancel or after `onConfirm` settles.
 *
 * Extracted from the pattern originally used ad hoc in
 * `AdminUserDetailPage`'s "Remove account" button, now shared across every
 * destructive admin action (disable account, remove account, delete route,
 * admin-unassign).
 */
export const ConfirmActionButton = ({
  label,
  confirmLabel,
  onConfirm,
  color = 'red',
  size = 'xs',
  fullWidth,
  disabled,
  noShadow,
}: ConfirmActionButtonProps) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  return (
    <Group gap={8} wrap="nowrap">
      <Button
        color={color}
        disabled={disabled}
        fullWidth={fullWidth}
        loading={submitting}
        onClick={handleClick}
        size={size}
        style={{
          boxShadow: noShadow ? 'none' : undefined,
          fontWeight: 700,
          letterSpacing: '0.04em',
        }}
        variant={confirming ? 'filled' : 'outline'}
      >
        {confirming ? confirmLabel : label}
      </Button>
      {confirming && (
        <ChipButton
          active={false}
          label={t('modals.confirmAction.cancel')}
          onClick={() => setConfirming(false)}
        />
      )}
    </Group>
  );
};
